// src/components/layout/DetailPageLayout.jsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../core/Breadcrumbs';

/**
 * Standardized detail page layout
 */
const DetailPageLayout = ({
  title,
  subtitle,
  breadcrumbs = [],
  actions = [], // Support both actions and actionButtons for compatibility
  actionButtons = [],
  stats = [], // Add stats support
  isLoading = false,
  error = null,
  children,
  onEdit,
  onDelete,
  canEdit = false,
  contentProps = {},
  isEmpty = false,
  emptyStateComponent = null
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }
  
  if (isEmpty && emptyStateComponent) {
    return emptyStateComponent;
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs 
          items={breadcrumbs.slice(0, -1)} // All except the last one
          current={breadcrumbs[breadcrumbs.length - 1]?.label} // Last one is current
          sx={{ mb: 3 }}
        />
      )}
      
      {/* Header with title and actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 3 
      }}>
        <Box>
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Use actions if provided, otherwise fall back to actionButtons */}
          {(actions.length > 0 ? actions : actionButtons).map((button, index) => (
            <Button
              key={index}
              variant={button.variant || "contained"}
              color={button.color || "primary"}
              startIcon={button.icon}
              onClick={button.onClick}
              component={button.to ? Link : undefined}
              to={button.to}
            >
              {button.label}
            </Button>
          ))}
          
          {canEdit && onEdit && (
            <Button 
              variant="outlined" 
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
          
          {canEdit && onDelete && (
            <Button 
              variant="outlined" 
              color="error" 
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Stats section */}
      {stats.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          mb: 4,
          flexWrap: 'wrap' 
        }}>
          {stats.map((stat, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minWidth: 200,
                flex: '1 1 0'
              }}
            >
              {stat.icon && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme => theme.palette[stat.color || 'primary'].light + '20',
                    color: theme => theme.palette[stat.color || 'primary'].main,
                    borderRadius: 2
                  }}
                >
                  {stat.icon}
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {stat.value}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
      
      {/* Content */}
      <Box {...contentProps}>
        {children}
      </Box>
    </Box>
  );
};

export default DetailPageLayout;